import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import type { Indirizzo } from '../api/scheda.js';
import { useSalvaIndirizzo } from '../api/scheda.js';
import { CampoBc } from '../componenti/CampoBc.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';

/**
 * La modale dell'indirizzo.
 *
 * È il posto dove i limiti di Business Central contano di più: destinazione,
 * via, città e CAP finiscono nell'ordine che parte verso BC, e se sono troppo
 * lunghi l'ordine viene rifiutato. Qui il problema si vede mentre si scrive.
 */
interface Props {
  identityId: number;
  indirizzo: Indirizzo | null;
  paesi: { id: string | number; label: string }[];
  limiti: Record<string, number>;
  onChiudi: () => void;
}

export function ModaleIndirizzo({ identityId, indirizzo, paesi, limiti, onChiudi }: Props) {
  const [campi, setCampi] = useState({
    name: indirizzo?.name ?? '',
    countryId: indirizzo?.countryId ?? 0,
    address: indirizzo?.address ?? '',
    addressExtra: indirizzo?.addressExtra ?? '',
    city: indirizzo?.city ?? '',
    postalCode: indirizzo?.postalCode ?? '',
    provinceState: indirizzo?.provinceState ?? '',
    destinationCode: indirizzo?.destinationCode ?? '',
    isMain: indirizzo?.isMain ?? false,
  });

  const salva = useSalvaIndirizzo(identityId);
  const errore = salva.error instanceof ErroreApi ? salva.error : null;
  const cambia = (chiave: keyof typeof campi) => (valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const invia = () => {
    salva.mutate(
      {
        ...(indirizzo ? { id: indirizzo.id } : {}),
        name: campi.name.trim(),
        countryId: Number(campi.countryId),
        address: campi.address.trim() || null,
        addressExtra: campi.addressExtra.trim() || null,
        city: campi.city.trim() || null,
        postalCode: campi.postalCode.trim() || null,
        provinceState: campi.provinceState.trim() || null,
        destinationCode: campi.destinationCode.trim() || null,
        isMain: campi.isMain,
      },
      { onSuccess: onChiudi },
    );
  };

  return (
    <Modale
      larga
      occhiello={indirizzo ? 'Modifica indirizzo' : 'Nuovo indirizzo'}
      titolo={indirizzo?.name || 'Indirizzo'}
      nota={t('I limiti di lunghezza sono quelli di Business Central: se li superi, l\'ordine viene rifiutato al momento dell\'invio.')}
      inCorso={salva.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn btn-ghost" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <span className="spacer" />
          <button className="btn btn-primary" onClick={invia} disabled={salva.isPending}>
            {salva.isPending ? 'Salvo…' : 'Salva indirizzo'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="fields">
        <CampoBc
          id="i-nome"
          etichetta="Denominazione"
          obbligatorio
          larghezzaPiena
          valore={campi.name}
          limite={limiti.name}
          onCambia={cambia('name')}
        />

        <div className="field">
          <label htmlFor="i-paese">
            {t('Paese')} <span className="req">*</span>
          </label>
          <select
            className="select"
            id="i-paese"
            value={campi.countryId}
            onChange={(e) => setCampi((c) => ({ ...c, countryId: Number(e.target.value) }))}
          >
            <option value={0}>{t('Scegli il paese…')}</option>
            {paesi.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <CampoBc
          id="i-dest"
          etichetta="Codice destinazione"
          monospazio
          valore={campi.destinationCode}
          limite={limiti.destinationCode}
          onCambia={cambia('destinationCode')}
        />

        <CampoBc
          id="i-via"
          etichetta="Indirizzo"
          larghezzaPiena
          valore={campi.address}
          limite={limiti.address}
          onCambia={cambia('address')}
        />
        <CampoBc
          id="i-via2"
          etichetta="Indirizzo — seconda riga"
          larghezzaPiena
          valore={campi.addressExtra}
          limite={limiti.addressExtra}
          onCambia={cambia('addressExtra')}
        />

        <CampoBc
          id="i-citta"
          etichetta="Città"
          valore={campi.city}
          limite={limiti.city}
          onCambia={cambia('city')}
        />
        <CampoBc
          id="i-prov"
          etichetta="Provincia o stato"
          monospazio
          valore={campi.provinceState}
          limite={limiti.provinceState}
          onCambia={cambia('provinceState')}
        />
        <CampoBc
          id="i-cap"
          etichetta="CAP o ZIP"
          monospazio
          valore={campi.postalCode}
          limite={limiti.postalCode}
          onCambia={cambia('postalCode')}
        />
      </div>

      <div className="switch-row" style={{ marginTop: 10 }}>
        <button
          className="switch"
          role="switch"
          aria-checked={campi.isMain}
          onClick={() => setCampi((c) => ({ ...c, isMain: !c.isMain }))}
        />
        <span>{t('Indirizzo principale — è quello che compare in elenco e nelle stampe')}</span>
      </div>
    </Modale>
  );
}
